import { Component, DestroyRef, inject, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { MatTabChangeEvent, MatTabGroup, MatTab, MatTabContent } from '@angular/material/tabs';
import { ActivatedRoute, Params, Router } from '@angular/router';
import { AppInsightsServiceInjector } from '@next/core-lib/app-insights';
import { TranslatePipe } from '@next/core-lib/i18n';
import { tap } from 'rxjs';

import { AdviceMouseClickEvent } from 'src/app/shared/components/disease-overview-table.component/models/models';

import { createDownyMildewTelemetryProperties, DownyMildewAIIdentifiers } from '../../shared/constants';
import { DownyMildewSignalStore } from '../../signal-store/downy-mildew-signal-store';
import { PageToolbarComponent } from '@next/core-lib/components';
import { NgTemplateOutlet } from '@angular/common';
import { OverviewComponent } from './components/overview/overview.component';
import { DownyMildewInsightPageComponent } from './components/downy-mildew-insight-page/downy-mildew-insight-page.component';
import { CropYearSelectorComponent } from '../../../shared/components/crop-year-selector/crop-year-selector.component';

@Component({
  selector: 'app-downy-mildew-advice',
  templateUrl: './advice.component.html',
  styleUrl: './advice.component.scss',
  imports: [
    PageToolbarComponent,
    CropYearSelectorComponent,
    MatTabGroup,
    MatTab,
    NgTemplateOutlet,
    MatTabContent,
    OverviewComponent,
    DownyMildewInsightPageComponent,
    TranslatePipe,
  ],
})
export class AdviceComponent {
  private readonly destroyRef = inject(DestroyRef);
  private readonly store = inject(DownyMildewSignalStore);
  private readonly route = inject(ActivatedRoute);
  private readonly router = inject(Router);
  private readonly ai = inject(AppInsightsServiceInjector);

  private static readonly tabs = ['overview', 'insights'];

  protected readonly selectedTabIndex = signal(0);
  protected readonly lastSelectedCropField = signal<string | null>(null);
  private readonly hasTrackedPageView = signal(false);
  private readonly lastTrackedTab = signal<string | null>(null);

  protected readonly pageTitle = this.store.pageTitle;

  constructor() {
    this.route.queryParams
      .pipe(
        tap((params) => this.updateLastSelectedCropField(params)),
        tap((params: Params) => this.updateSelectedTab(params)),
        tap((params: Params) => this.trackUsageTelemetry(params)),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe();
  }

  protected async onTabChange(event: MatTabChangeEvent): Promise<void> {
    const selectedTab = AdviceComponent.tabs[event.index];
    this.selectedTabIndex.set(event.index);

    await this.router.navigate([], {
      queryParams: { tab: selectedTab, cropFieldId: this.lastSelectedCropField() },
      queryParamsHandling: 'merge',
    });
  }

  protected async onCropFieldClick(event: AdviceMouseClickEvent): Promise<void> {
    await this.router.navigate([], {
      queryParams: { tab: 'insights', cropFieldId: event.cropField.cropFieldId },
      queryParamsHandling: 'merge',
    });
  }

  private updateSelectedTab(params: Params): void {
    const tabName = params['tab'] || 'overview';
    const tabIndex = AdviceComponent.tabs.indexOf(tabName);
    this.selectedTabIndex.set(tabIndex !== -1 ? tabIndex : 0);
  }

  private async updateLastSelectedCropField(params: Params): Promise<void> {
    if (params['cropFieldId']) {
      this.lastSelectedCropField.set(params['cropFieldId']);
      return;
    }

    if (this.lastSelectedCropField()) {
      await this.router.navigate([], {
        queryParams: { cropFieldId: this.lastSelectedCropField() },
        queryParamsHandling: 'merge',
      });
    }
  }

  private trackUsageTelemetry(params: Params): void {
    const tabName = params['tab'] || 'overview';
    const normalizedTab = AdviceComponent.tabs.includes(tabName) ? tabName : 'overview';

    if (!this.hasTrackedPageView()) {
      this.ai.trackEvent(DownyMildewAIIdentifiers.pageViewed, createDownyMildewTelemetryProperties('pageViewed'));
      this.hasTrackedPageView.set(true);
    }

    if (this.lastTrackedTab() === normalizedTab) {
      return;
    }

    this.ai.trackEvent(
      DownyMildewAIIdentifiers.tabSelected,
      createDownyMildewTelemetryProperties('tabSelected', { tab: normalizedTab }),
    );

    if (normalizedTab === 'overview') {
      this.ai.trackEvent(
        DownyMildewAIIdentifiers.overviewPageViewed,
        createDownyMildewTelemetryProperties('overviewPageViewed', { tab: 'overview' }),
      );
    } else {
      this.ai.trackEvent(
        DownyMildewAIIdentifiers.insightsPageViewed,
        createDownyMildewTelemetryProperties('insightsPageViewed', { tab: 'insights' }),
      );
    }

    this.lastTrackedTab.set(normalizedTab);
  }
}
